import {SessionChangedError, type TabIdentity, TabSession} from './tabSession';
import {tabSession} from './session';

export type TaskWindowResult = 'opened' | 'focused' | 'blocked' | 'unsafe';

/**
 * A task window inherits the validated short-lived pair, but never the copied
 * Auth0 refresh family. It can reauthenticate independently if it outlives the pair.
 */
export class TaskWindowLauncher {
    private readonly windows = new Map<string, Window>();

    constructor(private readonly source: Window, private readonly session: TabSession) {}

    open(taskId: string): TaskWindowResult {
        let operation;
        try { operation = this.session.capture(); }
        catch { return 'unsafe'; }

        const key = this.key(operation.identity, taskId);
        const existing = this.windows.get(key);
        if (existing && !existing.closed) {
            try {
                operation.assertCurrent();
                if (existing.location.origin === this.source.location.origin &&
                    this.session.storageHasIdentity(existing.sessionStorage, operation.identity)) {
                    existing.focus();
                    return 'focused';
                }
            } catch { /* Leave an inaccessible or differently bound window untouched. */ }
        }
        this.windows.delete(key);

        const width = 1000;
        const height = 700;
        const left = this.source.screenX + Math.round((this.source.outerWidth - width) / 2);
        const top = this.source.screenY + Math.round((this.source.outerHeight - height) / 2);
        const name = `task_${taskId}_${crypto.randomUUID()}`;
        const child = this.source.open('', name, `width=${width},height=${height},left=${left},top=${top}`);
        if (!child) return 'blocked';

        try {
            operation.assertCurrent();
            if (!this.session.storageHasIdentity(child.sessionStorage, operation.identity)) {
                throw new SessionChangedError();
            }
            const prefix = `${this.session.namespace}:sdk:`;
            const copiedSdkKeys: string[] = [];
            for (let index = 0; index < child.sessionStorage.length; index++) {
                const storageKey = child.sessionStorage.key(index);
                if (storageKey?.startsWith(prefix)) copiedSdkKeys.push(storageKey);
            }
            for (const storageKey of copiedSdkKeys) child.sessionStorage.removeItem(storageKey);
            operation.assertCurrent();
            child.opener = null;
            child.location.replace(`${this.source.location.origin}/tasks?item=${encodeURIComponent(taskId)}`);
            this.windows.set(key, child);
            return 'opened';
        } catch {
            // This is a newly-created blank. Never load the app with a partial scrub.
            try { child.close(); } catch { /* The browser may already have closed it. */ }
            return 'unsafe';
        }
    }

    private key(identity: Readonly<TabIdentity>, taskId: string): string {
        return JSON.stringify([identity.subject, identity.firmId, identity.userId, identity.portal, taskId]);
    }
}

const launcher = new TaskWindowLauncher(window, tabSession);
export const openTaskWindow = (taskId: string): TaskWindowResult => launcher.open(taskId);
